import { useState, useRef, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecordingStore } from '@/store/recording-store'
import { useEditorStore } from '@/store/editor-store'
import { useProjectStore } from '@/store/project-store'

export type DesktopRecordingStatus = 'idle' | 'countdown' | 'recording' | 'paused' | 'processing' | 'stopped'

export interface DesktopSource {
    id: string
    name: string
    thumbnailDataUrl: string
    appIconDataUrl: string | null
    display_id: string
}

export interface DesktopRecordingOptions {
    sourceId: string
    audio: boolean
    micDeviceId?: string
    frameRate: number
    cameraDeviceId?: string
    systemAudio?: boolean
    cropRect?: { x: number; y: number; w: number; h: number }
}

const MIME_TYPES = [
    'video/webm;codecs=vp9,opus',          // VP9 + Opus — most reliable in Electron
    'video/webm;codecs=vp8,opus',          // VP8 + Opus — broad fallback
    'video/webm',                           // browser-default (includes audio)
    'video/mp4;codecs=avc1,mp4a.40.2',   // H.264 + AAC (Electron 36+ only, broken before)
]

function getSupportedMimeType(): string {
    return MIME_TYPES.find((t) => MediaRecorder.isTypeSupported(t)) ?? 'video/webm'
}

export function useDesktopRecorder() {
    const navigate = useNavigate()
    const recordingStore = useRecordingStore()
    const editorStore = useEditorStore()
    const { createProject } = useProjectStore()

    const [status, setStatus] = useState<DesktopRecordingStatus>('idle')
    const [duration, setDuration] = useState(0)
    const [sources, setSources] = useState<DesktopSource[]>([])
    const [isMicOn, setIsMicOn] = useState(true)

    const mediaRecorderRef = useRef<MediaRecorder | null>(null)
    const streamRef = useRef<MediaStream | null>(null)
    const chunksRef = useRef<Blob[]>([])
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const startTimeRef = useRef(0)
    const pausedAtRef = useRef(0)
    const pauseStartRef = useRef(0)

    // Audio mixing refs
    const micStreamRef = useRef<MediaStream | null>(null)
    const audioContextRef = useRef<AudioContext | null>(null)

    // Camera recording refs
    const cameraStreamRef = useRef<MediaStream | null>(null)
    const cameraRecorderRef = useRef<MediaRecorder | null>(null)
    const cameraChunksRef = useRef<Blob[]>([])
    const cameraStoppedRef = useRef<Promise<void>>(Promise.resolve())

    // Area crop refs
    const cropVideoRef = useRef<HTMLVideoElement | null>(null)
    const cropAnimRef = useRef<number | null>(null)
    const cropRectRef = useRef<{ x: number; y: number; w: number; h: number } | null>(null)

    const electron = (window as any).electron

    const loadSources = useCallback(async () => {
        if (!electron?.getSources) return []
        try {
            const srcs: DesktopSource[] = await electron.getSources()
            setSources(srcs)
            return srcs
        } catch (err) {
            console.error('[useDesktopRecorder] Failed to load sources:', err)
            return []
        }
    }, [electron])

    const startRecording = useCallback(async (options: DesktopRecordingOptions) => {
        try {
            let screenW = 1920, screenH = 1080
            if (electron?.getScreenDimensions) {
                const dims = await electron.getScreenDimensions()
                screenW = Math.round(dims.width * (dims.scaleFactor || 1))
                screenH = Math.round(dims.height * (dims.scaleFactor || 1))
            }

            if (electron?.setSelectedSource) {
                await electron.setSelectedSource(options.sourceId)
            }

            if (electron?.setSystemAudio) {
                await electron.setSystemAudio(options.systemAudio ?? false)
            }

            const displayStream = await navigator.mediaDevices.getDisplayMedia({
                video: { frameRate: { ideal: options.frameRate, max: options.frameRate }, cursor: 'never' } as any,
                audio: options.systemAudio ? { echoCancellation: false, noiseSuppression: false, autoGainControl: false } : false,
            })

            // Microphone — captured separately, mixed via AudioContext below
            let micTracks: MediaStreamTrack[] = []
            if (options.audio) {
                try {
                    const audioConstraints = options.micDeviceId
                        ? { deviceId: { exact: options.micDeviceId } }
                        : true
                    const micStream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints, video: false })
                    micStreamRef.current = micStream
                    micTracks = micStream.getAudioTracks()
                    console.log('[useDesktopRecorder] Mic tracks:', micTracks.length, micTracks.map(t => t.label))
                    if (micTracks.length === 0) {
                        console.warn('[useDesktopRecorder] getUserMedia succeeded but 0 audio tracks — mic blocked at OS level (Windows Settings → Privacy → Microphone)')
                    }
                } catch (e: any) {
                    console.error('[useDesktopRecorder] Microphone error:', e?.name, e?.message)
                }
            }

            // Camera — recorded as a separate stream/blob
            cameraChunksRef.current = []
            cameraStoppedRef.current = Promise.resolve()
            if (options.cameraDeviceId) {
                try {
                    const camStream = await navigator.mediaDevices.getUserMedia({
                        video: { deviceId: { exact: options.cameraDeviceId }, width: { ideal: 1280 }, height: { ideal: 720 } },
                        audio: false,
                    })
                    cameraStreamRef.current = camStream
                    const camMime = getSupportedMimeType()
                    const camRecorder = new MediaRecorder(camStream, { mimeType: camMime })
                    cameraRecorderRef.current = camRecorder
                    camRecorder.ondataavailable = (e) => {
                        if (e.data.size > 0) cameraChunksRef.current.push(e.data)
                    }
                    // Track when camera recorder finishes so finalize can wait for it
                    cameraStoppedRef.current = new Promise<void>(resolve => {
                        camRecorder.addEventListener('stop', () => resolve(), { once: true })
                    })
                    camRecorder.start(500)
                } catch (e) {
                    console.warn('[useDesktopRecorder] Camera unavailable:', e)
                }
            }

            // Area crop — canvas-draw the display stream cropped to the selected rect
            cropRectRef.current = options.cropRect ?? null
            let finalVideoTracks = displayStream.getVideoTracks()
            if (options.cropRect) {
                const { x, y, w, h } = options.cropRect
                const canvas = document.createElement('canvas')
                canvas.width = w
                canvas.height = h
                const ctx = canvas.getContext('2d')!
                const videoEl = document.createElement('video')
                videoEl.srcObject = new MediaStream(displayStream.getVideoTracks())
                videoEl.muted = true
                await new Promise<void>(res => { videoEl.onloadedmetadata = () => { videoEl.play().then(() => res()) } })
                cropVideoRef.current = videoEl
                const canvasStream = canvas.captureStream(options.frameRate)
                const drawFrame = () => {
                    ctx.drawImage(videoEl, x, y, w, h, 0, 0, w, h)
                    cropAnimRef.current = requestAnimationFrame(drawFrame)
                }
                cropAnimRef.current = requestAnimationFrame(drawFrame)
                finalVideoTracks = canvasStream.getVideoTracks()
                screenW = w
                screenH = h
            }

            // Mix all audio sources via AudioContext — canonical approach used by Loom/all Electron recorders.
            // Passing multiple raw audio tracks to MediaRecorder has undefined behaviour per spec.
            const systemAudioTracks = displayStream.getAudioTracks()
            let mixedAudioTracks: MediaStreamTrack[] = []

            if (systemAudioTracks.length > 0 || micTracks.length > 0) {
                try {
                    const audioCtx = new AudioContext()
                    audioContextRef.current = audioCtx
                    const destination = audioCtx.createMediaStreamDestination()

                    if (systemAudioTracks.length > 0) {
                        const sysSource = audioCtx.createMediaStreamSource(new MediaStream(systemAudioTracks))
                        sysSource.connect(destination)
                        console.log('[useDesktopRecorder] System audio tracks:', systemAudioTracks.length)
                    }
                    if (micTracks.length > 0) {
                        const micSource = audioCtx.createMediaStreamSource(new MediaStream(micTracks))
                        micSource.connect(destination)
                        console.log('[useDesktopRecorder] Mic tracks mixed into AudioContext')
                    }
                    mixedAudioTracks = destination.stream.getAudioTracks()
                } catch (e) {
                    console.warn('[useDesktopRecorder] AudioContext merge failed, falling back to raw tracks:', e)
                    mixedAudioTracks = [...systemAudioTracks, ...micTracks]
                }
            }

            const stream = new MediaStream([
                ...finalVideoTracks,
                ...mixedAudioTracks,
            ])
            streamRef.current = stream
            chunksRef.current = []

            console.log('[useDesktopRecorder] Final stream tracks:', stream.getTracks().map(t => `${t.kind}:${t.label}`))

            if (electron?.startCursorTracking) {
                await electron.startCursorTracking()
            }

            const mimeType = getSupportedMimeType()
            console.log('[useDesktopRecorder] MIME type:', mimeType)
            const recorder = new MediaRecorder(stream, { mimeType })
            mediaRecorderRef.current = recorder

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) chunksRef.current.push(e.data)
            }

            recorder.onstop = async () => {
                // Wait for camera recorder to finish collecting its last chunk
                await cameraStoppedRef.current
                await finalize(mimeType, screenW, screenH)
            }

            await electron?.setWindowCapture?.(false)

            recorder.start(500)
            startTimeRef.current = Date.now()
            setStatus('recording')
            setDuration(0)

            timerRef.current = setInterval(() => {
                setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000))
            }, 500)

        } catch (err) {
            console.error('[useDesktopRecorder] startRecording failed:', err)
            cleanupStream()
            throw err
        }
    }, [electron])

    const pauseRecording = useCallback(() => {
        if (!mediaRecorderRef.current || status !== 'recording') return
        mediaRecorderRef.current.pause()
        cameraRecorderRef.current?.pause()
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
        pausedAtRef.current = Date.now() - startTimeRef.current
        pauseStartRef.current = Date.now()
        setStatus('paused')
    }, [status])

    const resumeRecording = useCallback(() => {
        if (!mediaRecorderRef.current || status !== 'paused') return
        mediaRecorderRef.current.resume()
        cameraRecorderRef.current?.resume()
        startTimeRef.current = Date.now() - pausedAtRef.current
        timerRef.current = setInterval(() => {
            setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000))
        }, 500)
        setStatus('recording')
    }, [status])

    const stopRecording = useCallback(async () => {
        if (!mediaRecorderRef.current || (status !== 'recording' && status !== 'paused')) return
        setStatus('processing')
        if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
        }
        // Stop camera first so its onstop fires; main recorder onstop waits for it
        if (cameraRecorderRef.current && cameraRecorderRef.current.state !== 'inactive') {
            cameraRecorderRef.current.stop()
        }
        mediaRecorderRef.current.stop()
    }, [status])

    const cancelRecording = useCallback(() => {
        if (timerRef.current) clearInterval(timerRef.current)
        mediaRecorderRef.current?.stop()
        cameraRecorderRef.current?.stop()
        cleanupStream()
        setStatus('idle')
        setDuration(0)
        chunksRef.current = []
        cameraChunksRef.current = []
    }, [])

    const toggleMic = useCallback((on: boolean) => {
        setIsMicOn(on)
        // Toggle original mic tracks — AudioContext source node picks up .enabled changes live
        micStreamRef.current?.getAudioTracks().forEach((t) => { t.enabled = on })
    }, [])

    async function finalize(mimeType: string, screenW: number, screenH: number) {
        const durationSec = (Date.now() - startTimeRef.current) / 1000

        let mouseData: any[] = []
        if (electron?.stopCursorTracking) {
            try {
                mouseData = await electron.stopCursorTracking()
            } catch {}
        }

        // Offset cursor coords to be relative to the crop region
        const savedCropRect = cropRectRef.current
        cleanupStream()
        if (savedCropRect && mouseData.length > 0) {
            mouseData = mouseData.map((e: any) => ({ ...e, x: e.x - savedCropRect.x, y: e.y - savedCropRect.y }))
        }

        const blob = new Blob(chunksRef.current, { type: mimeType })
        chunksRef.current = []

        // Collect camera blob if recorded
        const cameraBlob = cameraChunksRef.current.length > 0
            ? new Blob(cameraChunksRef.current, { type: mimeType })
            : null
        cameraChunksRef.current = []

        editorStore.reset()
        recordingStore.reset()

        recordingStore.setBlob(blob)
        recordingStore.setMouseData(mouseData)
        recordingStore.setScreenDimensions(screenW, screenH)
        recordingStore.setDuration(durationSec)
        recordingStore.setDesktop(true)
        recordingStore.setStatus('stopped')

        if (cameraBlob) {
            recordingStore.setCameraBlob(cameraBlob)
            editorStore.updateCameraLayer({ visible: true })
        }

        // Trim 1s from start and end to prevent any HUD visibility at boundaries
        const TRIM = 1
        if (editorStore.initClips) {
            editorStore.initClips(durationSec)
            const trimStart = TRIM
            const trimEnd = Math.max(durationSec - TRIM, trimStart + 0.1)
            useEditorStore.setState((state) => ({
                clips: state.clips.map((c, i) =>
                    i === 0 ? { ...c, start: trimStart, end: trimEnd } : c
                ),
                duration: trimEnd - trimStart,
            }))
        } else {
            editorStore.setDuration(durationSec)
        }
        editorStore.updateCursorSettings({ visible: true }) // Desktop WGC hides native cursor, show custom overlay by default!

        if (mouseData.length > 0 && editorStore.generateAutoZoom) {
            editorStore.generateAutoZoom(mouseData as any, screenW, screenH)
        }

        await electron?.setWindowCapture?.(true)

        setStatus('stopped')
        electron?.showEditor?.()
        navigate('/editor')

        createProject('Desktop Recording').catch((err) => {
            console.error('[desktop] project disk save failed:', err)
        })
    }

    function cleanupStream() {
        if (cropAnimRef.current) { cancelAnimationFrame(cropAnimRef.current); cropAnimRef.current = null }
        if (cropVideoRef.current) { cropVideoRef.current.srcObject = null; cropVideoRef.current = null }
        cropRectRef.current = null
        streamRef.current?.getTracks().forEach((t) => t.stop())
        streamRef.current = null
        mediaRecorderRef.current = null
        micStreamRef.current?.getTracks().forEach((t) => t.stop())
        micStreamRef.current = null
        audioContextRef.current?.close()
        audioContextRef.current = null
        cameraStreamRef.current?.getTracks().forEach((t) => t.stop())
        cameraStreamRef.current = null
        cameraRecorderRef.current = null
    }

    useEffect(() => {
        return () => {
            if (timerRef.current) clearInterval(timerRef.current)
            cleanupStream()
        }
    }, [])

    return {
        status,
        duration,
        sources,
        isMicOn,
        loadSources,
        startRecording,
        pauseRecording,
        resumeRecording,
        stopRecording,
        cancelRecording,
        toggleMic,
    }
}
